import { FunctionComponent, useState } from "react";
import {
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  Position,
  useStore,
  XYPosition,
} from "reactflow";
import { useLineageContext } from "../context/LineageProvider";
import { RefSource } from "../types";
import { markerRefUrl, REF_SOURCES_ALL } from "../utils";
import { CorroborationPanel } from "./CorroborationPanel";

/**
 * Routing for ERD overlay refs picks one of two strategies based on
 * `data.isColumnAnchored`:
 *
 *   - **Column-anchored** (both endpoints are column rows): classic ER
 *     stepped routing — edge exits horizontally from the column row's
 *     left or right side (whichever faces the target table), jogs
 *     vertically, then approaches the target column row from the side.
 *     Each edge gets a small per-id offset on the vertical jog so
 *     multiple edges leaving the same column row don't overlap pixel-
 *     for-pixel. Reads as a real ER diagram.
 *
 *   - **Table-anchored** (one or both endpoints is a whole table): a
 *     floating bezier — anchor points are computed dynamically as the
 *     intersection of each node's bounding box with the line from one
 *     center to the other. Skirts around node bodies; better than
 *     fixed right→left handles when tables aren't horizontally aligned.
 *
 * Marker IDs and stroke styling come from the edge's `markerStart` /
 * `markerEnd` / `style` (set in `createRefEdge`); this component just
 * supplies the path.
 */
export const RefEdge: FunctionComponent<EdgeProps> = ({
  id,
  source,
  target,
  data,
  style,
}) => {
  const sourceNode = useStore(s => s.nodeInternals.get(source));
  const targetNode = useStore(s => s.nodeInternals.get(target));
  const {
    state: { enabledRefSources },
  } = useLineageContext();
  const [hovered, setHovered] = useState(false);

  if (!sourceNode || !targetNode) return null;

  const refData = data as
    | {
        isColumnAnchored?: boolean;
        markerEndId?: string;
        markerStartId?: string;
        corroboratingSources?: RefSource[];
      }
    | undefined;
  const { path, midX, midY } = refData?.isColumnAnchored
    ? steppedColumnPath(sourceNode, targetNode, id)
    : floatingBezierPath(sourceNode, targetNode);

  // Render the SVG path ourselves rather than delegating to BaseEdge.
  // ReactFlow's edge wrapper rewraps `markerEnd` / `markerStart` props in
  // `url('#…')` before they reach a custom edge component, which mangles
  // the absolute URL we need for VS Code webviews (their `<base>` tag
  // breaks fragment-only refs). Setting the attribute directly gives us
  // the unmangled value.
  const markerEndAttr = refData?.markerEndId ? markerRefUrl(refData.markerEndId) : undefined;
  const markerStartAttr = refData?.markerStartId
    ? markerRefUrl(refData.markerStartId)
    : undefined;

  // Hover-only corroboration panel. Shows colored pills for each source
  // that *currently has its toggle on* AND contributes to this edge.
  // Disabled sources are filtered out per user preference — dots never
  // show for sources you've turned off in the popover.
  const corroborating = new Set(refData?.corroboratingSources ?? []);
  const visibleSources = REF_SOURCES_ALL.filter(
    s => enabledRefSources[s] && corroborating.has(s)
  );

  return (
    <g
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <path
        id={id}
        className="react-flow__edge-path"
        d={path}
        fill="none"
        style={style}
        markerEnd={markerEndAttr}
        markerStart={markerStartAttr}
      />
      {/* Wider invisible interaction path so the user doesn't have to hit
          the visible 1.5 px stroke exactly to trigger hover. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        pointerEvents="stroke"
      />
      {hovered && visibleSources.length > 0 ? (
        <CorroborationPanel x={midX} y={midY} sources={visibleSources} />
      ) : null}
    </g>
  );
};


/**
 * Pixel gap between the line endpoint and the node border. With the
 * marker's wide end anchored at the line tip (per ER convention), the
 * marker visually "sits on" the node border. Just 1 px of breathing
 * room avoids the marker's stroke clipping into the node.
 */
const NODE_OUTSET = 1;

/**
 * Stepped right→left (or left→right) routing for column-row endpoints.
 * Anchor x is the column-row's left or right border depending on which
 * side of the source the target sits on. Anchor y is the column-row's
 * vertical center. The per-id offset spreads the vertical jog laterally
 * for parallel edges leaving the same row.
 */
interface PathResult {
  path: string;
  midX: number;
  midY: number;
}

function steppedColumnPath(
  source: NodeWithGeometry,
  target: NodeWithGeometry,
  id: string
): PathResult {
  const sPos = source.positionAbsolute ?? source.position;
  const tPos = target.positionAbsolute ?? target.position;
  const sw = source.width ?? 0;
  const sh = source.height ?? 0;
  const tw = target.width ?? 0;
  const th = target.height ?? 0;

  const sourceLeftOfTarget = sPos.x + sw / 2 < tPos.x + tw / 2;

  const sx = sourceLeftOfTarget ? sPos.x + sw + NODE_OUTSET : sPos.x - NODE_OUTSET;
  const sy = sPos.y + sh / 2;
  const tx = sourceLeftOfTarget ? tPos.x - NODE_OUTSET : tPos.x + tw + NODE_OUTSET;
  const ty = tPos.y + th / 2;

  const offset = 12 + (hashId(id) % 7) * 4;

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourceLeftOfTarget ? Position.Right : Position.Left,
    targetX: tx,
    targetY: ty,
    targetPosition: sourceLeftOfTarget ? Position.Left : Position.Right,
    borderRadius: 6,
    offset,
  });
  return { path, midX: labelX, midY: labelY };
}

function floatingBezierPath(source: NodeWithGeometry, target: NodeWithGeometry): PathResult {
  const { sx, sy, tx, ty, sourcePos, targetPos } = computeFloatingEnds(source, target);
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const sxOut = sx + ux * NODE_OUTSET;
  const syOut = sy + uy * NODE_OUTSET;
  const txOut = tx - ux * NODE_OUTSET;
  const tyOut = ty - uy * NODE_OUTSET;
  const [path, labelX, labelY] = getBezierPath({
    sourceX: sxOut,
    sourceY: syOut,
    sourcePosition: sourcePos,
    targetX: txOut,
    targetY: tyOut,
    targetPosition: targetPos,
  });
  return { path, midX: labelX, midY: labelY };
}

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

interface NodeWithGeometry {
  positionAbsolute?: XYPosition;
  position: XYPosition;
  width?: number | null;
  height?: number | null;
}

/**
 * Compute the intersection of the line from one node's center to the
 * other's center with each node's bounding box. The result is the optimal
 * attachment point for a floating edge.
 *
 * Adapted from ReactFlow's floating-edge example
 * (https://reactflow.dev/examples/edges/floating-edges).
 */
function computeFloatingEnds(source: NodeWithGeometry, target: NodeWithGeometry) {
  const { x: sx, y: sy, position: sPos } = nodeIntersection(source, target);
  const { x: tx, y: ty, position: tPos } = nodeIntersection(target, source);
  return { sx, sy, tx, ty, sourcePos: sPos, targetPos: tPos };
}

function nodeIntersection(
  inner: NodeWithGeometry,
  outer: NodeWithGeometry
): { x: number; y: number; position: Position } {
  const innerPos = inner.positionAbsolute ?? inner.position;
  const outerPos = outer.positionAbsolute ?? outer.position;
  const w = inner.width ?? 0;
  const h = inner.height ?? 0;
  const ow = outer.width ?? 0;
  const oh = outer.height ?? 0;

  const x2 = innerPos.x + w / 2;
  const y2 = innerPos.y + h / 2;
  const x1 = outerPos.x + ow / 2;
  const y1 = outerPos.y + oh / 2;

  const dx = x1 - x2;
  const dy = y1 - y2;
  const wHalf = w / 2;
  const hHalf = h / 2;

  // Edge case — co-located centers; return center.
  if (dx === 0 && dy === 0) {
    return { x: x2, y: y2, position: Position.Right };
  }

  // Slope check determines which side the line crosses.
  const slope = dy / dx;
  const boxSlope = hHalf / wHalf;

  let x: number;
  let y: number;
  let position: Position;

  if (Math.abs(slope) < boxSlope) {
    // Crosses left or right side
    if (dx > 0) {
      x = x2 + wHalf;
      y = y2 + slope * wHalf;
      position = Position.Right;
    } else {
      x = x2 - wHalf;
      y = y2 - slope * wHalf;
      position = Position.Left;
    }
  } else {
    // Crosses top or bottom side
    if (dy > 0) {
      x = x2 + hHalf / slope;
      y = y2 + hHalf;
      position = Position.Bottom;
    } else {
      x = x2 - hHalf / slope;
      y = y2 - hHalf;
      position = Position.Top;
    }
  }

  return { x, y, position };
}

export const REF_EDGE_TYPE = "refEdge";
